// 江湖名号与绰号（作者 11-03：武侠江湖主题；名号要有趣、有特色，显示在每位玩家昵称下方）。
// 方案：genre-guandan/docs/个人档案与称号.md。名号跟掼蛋牌型挂钩、越往上越接近最大的牌型；只升不降，不做经验值。
// 纯函数，不碰界面与存档，便于测试。

/** 本机生涯战绩里与名号有关的字段（旧存档缺字段时按 0 补，见 normalizeCareer）。 */
export interface CareerLike {
  matches: number; wins: number; streak: number; best: number; rounds: number; firsts: number; bombs: number;
  golds: number; // 问鼎武林次数：一局里我方真打过 A（限副赛到时比级数不算）
  farthest: number; // 最远到过的级（2–14）
  wildCombos: number; // 用逢人配凑成炸弹或同花顺的次数
  comebacks: number; // 落后 5 级以上最后赢下比赛的次数
}

export interface Title { name: string; need: string; line: string; ok: (c: CareerLike) => boolean; progress: (c: CareerLike) => [number, number] }

const byWins = (n: number) => ({ ok: (c: CareerLike) => c.wins >= n, progress: (c: CareerLike): [number, number] => [Math.min(c.wins, n), n] });
const byGolds = (n: number) => ({ ok: (c: CareerLike) => c.golds >= n, progress: (c: CareerLike): [number, number] => [Math.min(c.golds, n), n] });

export const TITLES: Title[] = [
  { name: '洗牌童子', need: '新入江湖', line: '刚拜进山门，先把牌洗匀了再说。', ok: () => true, progress: () => [0, 0] },
  { name: '码牌学徒', need: '赢 1 局', line: '牌码得齐，心也就齐了。', ...byWins(1) },
  { name: '对子侠', need: '赢 5 局', line: '成双成对，出手不落单。', ...byWins(5) },
  { name: '顺子剑客', need: '赢 10 局', line: '一剑连五张，招招接得上。', ...byWins(10) },
  { name: '钢板金刚', need: '问鼎武林 1 次', line: '三同连张，硬得像块钢板。', ...byGolds(1) },
  { name: '炸弹堂主', need: '问鼎武林 3 次', line: '手里有炸，说话都有底气。', ...byGolds(3) },
  { name: '同花顺长老', need: '问鼎武林 6 次', line: '同花连顺，江湖罕见。', ...byGolds(6) },
  { name: '天王盟主', need: '问鼎武林 10 次', line: '四大天王在手，号令江湖。', ...byGolds(10) },
];

/** 当前名号的序号（只升不降：条件是累计数，本来就不会降）。 */
export function titleIndex(c: CareerLike): number {
  let i = 0;
  for (let k = 1; k < TITLES.length; k++) if (TITLES[k].ok(c)) i = k; else break;
  return i;
}

/** 离下一级还差多少：返回下一级名号与 [已有, 需要]；已是最高级返回 null。 */
export function nextTitle(c: CareerLike): { title: Title; have: number; need: number; hint: string } | null {
  const i = titleIndex(c);
  if (i >= TITLES.length - 1) return null;
  const t = TITLES[i + 1];
  const [have, need] = t.progress(c);
  const rest = need - have;
  const hint = i + 1 <= 3 ? `再赢 ${rest} 局成为「${t.name}」` : `再问鼎武林 ${rest} 次成为「${t.name}」`;
  return { title: t, have, need, hint };
}

/** 江湖绰号（彩蛋小徽章，可同时有多个，只做展示）。 */
export const NICKNAMES: { name: string; need: string; ok: (c: CareerLike) => boolean }[] = [
  { name: '霹雳炸手', need: '累计出炸弹 50 次', ok: (c) => c.bombs >= 50 },
  { name: '逢人配高手', need: '用逢人配凑成炸弹或同花顺 20 次', ok: (c) => c.wildCombos >= 20 },
  { name: '头游专业户', need: '打满 30 副、上游率 ≥ 40%', ok: (c) => c.rounds >= 30 && c.firsts / c.rounds >= 0.4 },
  { name: '铁打搭子', need: '连胜 5 局', ok: (c) => c.best >= 5 },
  { name: '绝地翻盘', need: '落后 5 级以上最后赢下比赛', ok: (c) => c.comebacks >= 1 },
];
export const nicknamesOf = (c: CareerLike): string[] => NICKNAMES.filter((n) => n.ok(c)).map((n) => n.name);

/** 旧存档补字段：不换键名、不清数据（HANDOFF：玩家记录升级不能丢）。 */
export function normalizeCareer<T extends Partial<CareerLike>>(raw: T): T & CareerLike {
  const z = { matches: 0, wins: 0, streak: 0, best: 0, rounds: 0, firsts: 0, bombs: 0, golds: 0, farthest: 2, wildCombos: 0, comebacks: 0 };
  const out = { ...z, ...raw } as T & CareerLike;
  for (const k of Object.keys(z) as (keyof CareerLike)[]) if (typeof out[k] !== 'number' || !Number.isFinite(out[k])) (out as CareerLike)[k] = z[k];
  return out;
}
